import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Alert,
  SelectChangeEvent
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StorageIcon from '@mui/icons-material/Storage';
import TerminalIcon from '@mui/icons-material/Terminal';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';

import { useTarefa } from '../../../../../contexts/TarefaContext';
import { IPosExecucaoTarefa, TipoPosExecucao, SituacaoExecucao } from '../../../../../types/tarefa';

const PosExecucaoForm = () => {
  const [tarefa, setTarefa] = useTarefa();

  // Estado para a nova ação que está sendo adicionada
  const [novoSituacao, setNovoSituacao] = useState<SituacaoExecucao>('Executada');
  const [novoTipo, setNovoTipo] = useState<TipoPosExecucao>('HistoricoBackupData7');
  const [novoCaminhoBat, setNovoCaminhoBat] = useState('');
  const [novoComandoBat, setComandoComandoBat] = useState('');

  // Erro de validação local
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  const posExecucoes = tarefa.posExecucao || [];

  const handleSituacaoChange = (event: SelectChangeEvent<SituacaoExecucao>) => {
    setNovoSituacao(event.target.value as SituacaoExecucao);
  };

  const handleTipoChange = (event: SelectChangeEvent<TipoPosExecucao>) => {
    setNovoTipo(event.target.value as TipoPosExecucao);
    setErroLocal(null);
  };

  const adicionarAcao = (e: React.FormEvent) => {
    e.preventDefault();
    setErroLocal(null);

    // Validação
    if (novoTipo === 'Bat' && !novoCaminhoBat.trim()) {
      setErroLocal('O caminho do arquivo script .bat é obrigatório.');
      return;
    }
    if (novoTipo === 'ComandoBat' && !novoComandoBat.trim()) {
      setErroLocal('O comando do sistema é obrigatório.');
      return;
    }

    const novaAcao: IPosExecucaoTarefa = {
      tipo: novoTipo,
      situacaoExecucao: novoSituacao
    };

    // Adiciona apenas as propriedades correspondentes
    if (novoTipo === 'Bat') {
      novaAcao.caminhoBat = novoCaminhoBat.trim();
    } else if (novoTipo === 'ComandoBat') {
      novaAcao.comandoBat = novoComandoBat.trim();
    }

    const novaLista = [...posExecucoes, novaAcao];
    setTarefa({
      ...tarefa,
      posExecucao: novaLista
    });

    // Limpar formulário de entrada
    setNovoCaminhoBat('');
    setComandoComandoBat('');
    setErroLocal(null);
  };

  const removerAcao = (index: number) => {
    const novaLista = posExecucoes.filter((_, idx) => idx !== index);
    setTarefa({
      ...tarefa,
      posExecucao: novaLista
    });
  };

  const getIconeTipo = (tipo: TipoPosExecucao) => {
    switch (tipo) {
      case 'HistoricoBackupData7':
        return <StorageIcon color="primary" />;
      case 'Bat':
        return <IntegrationInstructionsIcon color="secondary" />;
      case 'ComandoBat':
        return <TerminalIcon color="warning" />;
      default:
        return null;
    }
  };

  const getLabelTipo = (tipo: TipoPosExecucao) => {
    switch (tipo) {
      case 'HistoricoBackupData7':
        return 'Integração Data7 (Histórico)';
      case 'Bat':
        return 'Executar Script .bat';
      case 'ComandoBat':
        return 'Executar Comando do Sistema';
      default:
        return tipo;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Ações Pós-Execução (Opcional)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Configure rotinas automáticas adicionais que o agente Delphi executará localmente após a conclusão ou falha desta tarefa.
      </Typography>

      {/* Formulário de Adição */}
      <Card sx={{ mb: 4, border: '1px solid', borderColor: 'divider', boxShadow: 'none', borderRadius: '12px' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
            Configurar Nova Ação
          </Typography>

          {erroLocal && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {erroLocal}
            </Alert>
          )}

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="situacao-select-label">Executar Quando</InputLabel>
                <Select
                  labelId="situacao-select-label"
                  id="situacao-select"
                  value={novoSituacao}
                  label="Executar Quando"
                  onChange={handleSituacaoChange}
                >
                  <MenuItem value="Executada">Tarefa for concluída com sucesso</MenuItem>
                  <MenuItem value="Falha">Tarefa falhar</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={5}>
              <FormControl fullWidth size="small">
                <InputLabel id="tipo-select-label">Tipo de Ação</InputLabel>
                <Select
                  labelId="tipo-select-label"
                  id="tipo-select"
                  value={novoTipo}
                  label="Tipo de Ação"
                  onChange={handleTipoChange}
                >
                  <MenuItem value="HistoricoBackupData7">Integração Data7 (Registrar Histórico)</MenuItem>
                  <MenuItem value="Bat">Executar script de lote (.bat / .cmd)</MenuItem>
                  <MenuItem value="ComandoBat">Executar comando de prompt (WMIC, XCOPY...)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3} sx={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={adicionarAcao}
                fullWidth
              >
                Adicionar
              </Button>
            </Grid>

            {/* Campos condicionais baseados no Tipo selecionado */}
            {novoTipo === 'Bat' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Caminho Completo do Script (.bat)"
                  placeholder="Ex: C:\scripts\pos-backup.bat"
                  value={novoCaminhoBat}
                  onChange={(e) => setNovoCaminhoBat(e.target.value)}
                  required
                />
              </Grid>
            )}

            {novoTipo === 'ComandoBat' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Comando do Sistema"
                  placeholder="Ex: net send * 'Backup concluído!' ou copy backup.7z d:\destino"
                  value={novoComandoBat}
                  onChange={(e) => setComandoComandoBat(e.target.value)}
                  required
                />
              </Grid>
            )}

            {novoTipo === 'HistoricoBackupData7' && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  A integração Data7 realizará um comando SQL <code>INSERT INTO HistoricoBackup</code> automático no banco de dados configurado na tarefa principal para que o Data7 registre a realização do backup. Não há necessidade de comandos adicionais.
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ mb: 4 }} />

      {/* Lista de Ações Cadastradas */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
        Ações Configuradas ({posExecucoes.length})
      </Typography>

      {posExecucoes.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center', border: '2px dashed', borderColor: 'divider', borderRadius: '12px' }}>
          <Typography color="text.secondary" variant="body2">
            Nenhuma ação pós-execução configurada para esta tarefa.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {posExecucoes.map((acao, idx) => (
            <Card key={idx} variant="outlined" sx={{ borderRadius: '8px', boxShadow: 'none' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getIconeTipo(acao.tipo)}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {getLabelTipo(acao.tipo)}
                        </Typography>
                        <Chip
                          label={acao.situacaoExecucao === 'Executada' ? 'Sucesso' : 'Falha'}
                          color={acao.situacaoExecucao === 'Executada' ? 'success' : 'error'}
                          size="small"
                          sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }}
                        />
                      </Box>
                      {acao.tipo === 'Bat' && acao.caminhoBat && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          Arquivo: {acao.caminhoBat}
                        </Typography>
                      )}
                      {acao.tipo === 'ComandoBat' && acao.comandoBat && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          Comando: {acao.comandoBat}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <IconButton color="error" size="small" onClick={() => removerAcao(idx)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PosExecucaoForm;
